export interface NextStep {
  /** i18n key under common:helpWidget.steps */
  labelKey: string;
  /** Route suffix appended to the store or org base path */
  path: string;
  scope: 'store' | 'org';
}

export interface HelpResource {
  /** i18n key under common:helpWidget.titles */
  titleKey: string;
  video?: {
    loomId: string;
  };
  article?: {
    url: string;
    /** i18n key for the article link label */
    labelKey: string;
  };
  nextSteps?: NextStep[];
}

interface HelpRoute {
  pattern: RegExp;
  resource: HelpResource;
}

const helpRoutes: HelpRoute[] = [
  // --- Analytics / Overview (org root) ---
  {
    pattern: /\/org\/[^/]+$/,
    resource: {
      titleKey: 'analytics',
      video: { loomId: 'c0057638646348549b67adae51829dc9' },
      nextSteps: [
        { labelKey: 'addStore', path: '/stores', scope: 'org' },
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
      ],
    },
  },

  // --- Stores ---
  {
    pattern: /\/stores\/create$/,
    resource: {
      titleKey: 'addStore',
      video: { loomId: '690c90ad25bd4c29a59a2c3b2861ece3' },
      nextSteps: [
        { labelKey: 'connectShopify', path: '/integrations/shopify', scope: 'store' },
        { labelKey: 'addProducts', path: '/general/products', scope: 'store' },
      ],
    },
  },
  {
    pattern: /\/stores$/,
    resource: {
      titleKey: 'addStore',
      video: { loomId: '690c90ad25bd4c29a59a2c3b2861ece3' },
      nextSteps: [
        { labelKey: 'connectShopify', path: '/integrations/shopify', scope: 'store' },
        { labelKey: 'addProducts', path: '/general/products', scope: 'store' },
      ],
    },
  },

  // --- Integrations ---
  {
    pattern: /\/integrations\/shopify/,
    resource: {
      titleKey: 'connectShopify',
      video: { loomId: '4ca0a35a0b2841209e5ebc79974dcf49' },
      nextSteps: [
        { labelKey: 'addProducts', path: '/general/products', scope: 'store' },
        { labelKey: 'setupShippingTaxes', path: '/general/shippings', scope: 'store' },
      ],
    },
  },
  {
    pattern: /\/integrations$/,
    resource: {
      titleKey: 'integrations',
      video: { loomId: '7ba6f534c58e456dad987564128f7725' },
      nextSteps: [
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
        { labelKey: 'setupEmails', path: '/emails', scope: 'store' },
      ],
    },
  },

  // --- Products ---
  {
    pattern: /\/general\/products/,
    resource: {
      titleKey: 'connectProducts',
      video: { loomId: '8ba7f31a060c4899ab818aa2c9ba2a58' },
      nextSteps: [
        { labelKey: 'setupShippingTaxes', path: '/general/shippings', scope: 'store' },
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
      ],
    },
  },

  // --- Shipping & Taxes ---
  {
    pattern: /\/general\/shippings/,
    resource: {
      titleKey: 'shippingTaxes',
      video: { loomId: '76b28c1f6fbb48c7b2fcb0fded180440' },
      nextSteps: [
        { labelKey: 'setupDomain', path: '/domains', scope: 'org' },
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
      ],
    },
  },
  {
    pattern: /\/general\/taxes/,
    resource: {
      titleKey: 'shippingTaxes',
      video: { loomId: '76b28c1f6fbb48c7b2fcb0fded180440' },
      nextSteps: [
        { labelKey: 'setupDomain', path: '/domains', scope: 'org' },
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
      ],
    },
  },

  // --- Domain ---
  {
    pattern: /\/domains$/,
    resource: {
      titleKey: 'setupDomain',
      video: { loomId: 'e9a88489c25d4cb098949cdd8ba63654' },
      nextSteps: [
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
        { labelKey: 'createLandings', path: '/storefront/funnels', scope: 'store' },
      ],
    },
  },

  // --- Checkout / Payment Flows ---
  {
    pattern: /\/payment-flows\/[^/]+\/edit/,
    resource: {
      titleKey: 'customizeCheckout',
      video: { loomId: '05163b0577274cf8824ba2eb7f1a898c' },
      nextSteps: [
        { labelKey: 'createLandings', path: '/storefront/funnels', scope: 'store' },
        { labelKey: 'setupOrderBumps', path: '/general/upsells', scope: 'store' },
      ],
    },
  },
  {
    pattern: /\/payment-flows/,
    resource: {
      titleKey: 'setupCheckout',
      video: { loomId: 'fa14d414741f404a882570a5b71c7388' },
      nextSteps: [
        { labelKey: 'createLandings', path: '/storefront/funnels', scope: 'store' },
        { labelKey: 'setupOrderBumps', path: '/general/upsells', scope: 'store' },
      ],
    },
  },

  // --- Funnels & Storefront ---
  {
    pattern: /\/storefront\/funnels\/[^/]+$/,
    resource: {
      titleKey: 'funnelsOrchestration',
      video: { loomId: '6aeb551c25294b2d92bcfedf8f2c0bab' },
      nextSteps: [
        { labelKey: 'createLandings', path: '/storefront/funnels', scope: 'store' },
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
      ],
    },
  },
  {
    pattern: /\/storefront\/direct-links/,
    resource: {
      titleKey: 'directLinks',
      video: { loomId: 'ca3b908d547945309ec88556a981f1b8' },
      nextSteps: [
        { labelKey: 'createLandings', path: '/storefront/funnels', scope: 'store' },
        { labelKey: 'setupIntegrations', path: '/integrations', scope: 'store' },
      ],
    },
  },
  {
    pattern: /\/storefront(\/funnels)?$/,
    resource: {
      titleKey: 'createLandings',
      video: { loomId: '1c47926ca12e41ca8dc9103d989bc3fb' },
      nextSteps: [
        { labelKey: 'setupOrderBumps', path: '/general/upsells', scope: 'store' },
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
      ],
    },
  },

  // --- Offers & Post-Purchase ---
  {
    pattern: /\/general\/promotions\/offers/,
    resource: {
      titleKey: 'offersPostPurchase',
      video: { loomId: 'c156cdca972a406d981556e1d3d6853d' },
      nextSteps: [
        { labelKey: 'setupOrderBumps', path: '/general/upsells', scope: 'store' },
        { labelKey: 'setupDiscounts', path: '/general/promotions/discounts', scope: 'store' },
      ],
    },
  },
  {
    pattern: /\/general\/post-purchase/,
    resource: {
      titleKey: 'offersPostPurchase',
      video: { loomId: 'c156cdca972a406d981556e1d3d6853d' },
      nextSteps: [
        { labelKey: 'setupOrderBumps', path: '/general/upsells', scope: 'store' },
        { labelKey: 'setupDiscounts', path: '/general/promotions/discounts', scope: 'store' },
      ],
    },
  },

  // --- Order Bumps ---
  {
    pattern: /\/general\/upsells/,
    resource: {
      titleKey: 'setupOrderBumps',
      video: { loomId: '7542a85438f64811bd2ea838fd1b0c15' },
      nextSteps: [
        { labelKey: 'setupOffersPostPurchase', path: '/general/promotions/offers', scope: 'store' },
        { labelKey: 'setupDiscounts', path: '/general/promotions/discounts', scope: 'store' },
      ],
    },
  },

  // --- Discounts ---
  {
    pattern: /\/general\/promotions\/discounts/,
    resource: {
      titleKey: 'discounts',
      video: { loomId: '2077b0d8bff341c0bfc06b6db3200543' },
      nextSteps: [
        { labelKey: 'setupEmails', path: '/emails', scope: 'store' },
        { labelKey: 'setupIntegrations', path: '/integrations', scope: 'store' },
      ],
    },
  },

  // --- Emails ---
  {
    pattern: /\/settings\/emails/,
    resource: {
      titleKey: 'emails',
      video: { loomId: 'd6557606c09e4efc8ad862bbacc2e0b6' },
      nextSteps: [
        { labelKey: 'setupIntegrations', path: '/integrations', scope: 'store' },
        { labelKey: 'manageSubscriptions', path: '/subscriptions', scope: 'org' },
      ],
    },
  },

  // --- Payments (org-level) ---
  {
    pattern: /\/payments/,
    resource: {
      titleKey: 'payments',
      video: { loomId: '07f45cf9473b48718a9d39c749ea2e88' },
      nextSteps: [
        { labelKey: 'manageSubscriptions', path: '/subscriptions', scope: 'org' },
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
      ],
    },
  },

  // --- Orders (org-level, same payment video) ---
  {
    pattern: /\/orders/,
    resource: {
      titleKey: 'payments',
      video: { loomId: '07f45cf9473b48718a9d39c749ea2e88' },
      nextSteps: [
        { labelKey: 'manageSubscriptions', path: '/subscriptions', scope: 'org' },
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
      ],
    },
  },

  // --- Subscriptions (org-level) ---
  {
    pattern: /\/subscriptions/,
    resource: {
      titleKey: 'manageSubscriptions',
      video: { loomId: '3f9e87b2ac7b4aff8f3e58b34a02ea43' },
      nextSteps: [
        { labelKey: 'viewPayments', path: '/payments', scope: 'org' },
        { labelKey: 'setupCheckout', path: '/payment-flows', scope: 'org' },
      ],
    },
  },
];

export function getHelpResourceForRoute(pathname: string): HelpResource | null {
  for (const route of helpRoutes) {
    if (route.pattern.test(pathname)) {
      return route.resource;
    }
  }
  return null;
}
